import type { TokenPair } from "./types";

/** An error answer from the server, or a failure to reach it. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** True if the server could not be reached at all. */
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

const configured = (import.meta.env.VITE_API_URL as string | undefined)?.trim().replace(/\/+$/, "");

/** Where the API lives; empty means the address the dashboard was loaded from. */
export const API_ORIGIN = configured ?? "";
export const API_BASE = `${API_ORIGIN}/api/v1`;

/** The WebSocket address that belongs to a path on the API's origin. */
export function webSocketUrl(path: string): string {
  const origin = API_ORIGIN || window.location.origin;
  return origin.replace(/^http/, "ws") + path;
}

// --- tokens -------------------------------------------------------------------------------

const STORAGE_KEY = "droidmanage.tokens";

export interface StoredTokens {
  access: string;
  refresh: string;
}

export const tokens = {
  get(): StoredTokens | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? (JSON.parse(raw) as Partial<StoredTokens>) : null;
      return parsed?.access && parsed.refresh
        ? { access: parsed.access, refresh: parsed.refresh }
        : null;
    } catch {
      return null;
    }
  },
  set(pair: TokenPair): void {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ access: pair.access_token, refresh: pair.refresh_token }),
    );
    listeners.forEach((listener) => listener(true));
  },
  clear(): void {
    localStorage.removeItem(STORAGE_KEY);
    listeners.forEach((listener) => listener(false));
  },
};

type SessionListener = (signedIn: boolean) => void;
const listeners = new Set<SessionListener>();

/** Tells when the session starts, is renewed, or ends (also when the server ends it). */
export function onSessionChange(listener: SessionListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// --- requests -----------------------------------------------------------------------------

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  /** Sent as JSON. */
  body?: unknown;
  /** Sent as multipart form data (file uploads). */
  form?: FormData;
  query?: Query;
  signal?: AbortSignal;
  /** False for the calls that establish a session. */
  authenticated?: boolean;
}

function buildUrl(path: string, query?: Query): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  const suffix = search.size ? `?${search}` : "";
  return `${API_BASE}${path}${suffix}`;
}

async function toError(response: Response): Promise<ApiError> {
  let code = "http_error";
  let message = `The server answered ${response.status}`;
  let details: unknown;
  let requestId: string | undefined;
  try {
    const body = (await response.json()) as {
      error?: { code?: string; message?: string; details?: unknown; request_id?: string };
    };
    if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details;
      requestId = body.error.request_id ?? undefined;
    }
  } catch {
    // not JSON (a proxy's error page, for example): keep the generic message
  }
  return new ApiError(message, response.status, code, details, requestId);
}

async function send(path: string, options: RequestOptions, access: string | null): Promise<Response> {
  const headers = new Headers();
  if (access) headers.set("Authorization", `Bearer ${access}`);
  let body: BodyInit | undefined;
  if (options.form) {
    body = options.form; // the browser sets the multipart boundary itself
  } else if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(options.body);
  }
  try {
    return await fetch(buildUrl(path, options.query), {
      method: options.method ?? "GET",
      headers,
      body,
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError("The server can't be reached. Check the connection.", 0, "network");
  }
}

let renewal: Promise<string | null> | null = null;

/** Exchanges the refresh token for new tokens. Concurrent callers share one attempt. */
export function renewSession(): Promise<string | null> {
  renewal ??= (async () => {
    const current = tokens.get();
    if (!current) return null;
    try {
      const response = await send(
        "/auth/refresh",
        { method: "POST", body: { refresh_token: current.refresh } },
        null,
      );
      if (!response.ok) {
        // Only a refusal ends the session; a server that is down does not.
        if (response.status === 401) tokens.clear();
        return null;
      }
      const pair = (await response.json()) as TokenPair;
      tokens.set(pair);
      return pair.access_token;
    } catch {
      return null;
    } finally {
      renewal = null;
    }
  })();
  return renewal;
}

async function perform(path: string, options: RequestOptions): Promise<Response> {
  const authenticated = options.authenticated !== false;
  let response = await send(path, options, authenticated ? (tokens.get()?.access ?? null) : null);
  if (response.status === 401 && authenticated) {
    const renewed = await renewSession();
    if (renewed) response = await send(path, options, renewed);
  }
  if (!response.ok) throw await toError(response);
  return response;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await perform(path, options);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Fetches a file (screenshots, logs). Files need the token, so a plain link won't do. */
export async function download(path: string, signal?: AbortSignal): Promise<Blob> {
  return (await perform(path, { signal })).blob();
}

/** Every page of a list, for lists the dashboard wants whole (devices, groups). */
export async function requestAll<T>(
  path: string,
  query: Query = {},
  maxItems = 5000,
): Promise<T[]> {
  const items: T[] = [];
  const limit = 500;
  for (let offset = 0; offset < maxItems; offset += limit) {
    const page = await request<{ items: T[]; total: number }>(path, {
      query: { ...query, limit, offset },
    });
    items.push(...page.items);
    if (items.length >= page.total || page.items.length === 0) break;
  }
  return items;
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong";
}
